import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Search,
  Minus,
  Plus,
  Trash2,
  Gamepad2,
  Pause,
  Play,
  StopCircle,
  User,
  ChevronLeft,
  ShoppingCart,
} from "lucide-react";
import axios from "axios";
import { base_url, img_url } from "../../api/index";
import { printReceiptThermal } from "../../api/pos/receiptPrint";
import { usePosOrderRealtime } from "../../hook/usePosOrderRealtime";
import { useCanTakePayment } from "../../hook/useCanTakePayment";
import { PosLoader } from "./PosLoader";
import { usePermissions } from "../../context/PermissionsContext";
import { useConfirm } from "../ConfirmDialog";
import { useKitchenPicker } from "../KitchenPicker";
import { useManagerApproval } from "../ManagerApprovalDialog";
import { APPROVAL_SCOPES, needsManagerApproval } from "../../api/pos/managerApproval";
import { toast } from '../ui/notify';
import { Helmet } from "react-helmet";
import { useI18n } from "../../i18n/I18nContext";
import {
  ProductOptionsModal,
  OptionGroup,
  OptionSelection,
} from "./ProductOptionsModal";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const quickNoteKeys = [
  "pos.note.spicy",
  "pos.note.noIce",
  "pos.note.rare",
  "pos.note.extraSauce",
  "pos.note.takeaway",
];

const categoryIcons = [
  "☕",
  "🥤",
  "🍰",
  "🍟",
  "💨",
  "🎮",
  "🍖",
  "🥗",
  "🍳",
  "🍝",
  "🍕",
  "🧁",
];

/**
 * API'den gelen çok dilli isim objesinden string çıkarır.
 * Örnek: {"az": "Marqarita Pizza"} → "Marqarita Pizza"
 *        "Marqarita Pizza" → "Marqarita Pizza"
 */
const resolveName = (name: any): string => {
  if (!name) return "";
  if (typeof name === "string") {
    // JSON string olabilir: '{"az": "Marqarita Pizza"}'
    if (name.startsWith("{") && name.includes(":")) {
      try {
        const parsed = JSON.parse(name);
        if (typeof parsed === "object" && parsed !== null) {
          return (
            parsed.az ||
            parsed.en ||
            parsed.tr ||
            parsed.ru ||
            Object.values(parsed)[0] ||
            name
          );
        }
      } catch {
        // JSON değil, normal string
      }
    }
    return name;
  }
  if (typeof name === "object") {
    return (
      name.az || name.en || name.tr || name.ru || Object.values(name)[0] || ""
    );
  }
  return String(name);
};

/**
 * SƏBƏT SƏTRİNİN KİMLİYİ.
 *
 * Əvvəl sətir yalnız `id + type` ilə tanınırdı. Opsiyalar gələndə bu PUL İTKİSİDİR:
 *
 *   Marqarita (böyük + pendir)   ₼16,80 + ₼2,50
 *   Marqarita (böyük + göbələk)  ₼16,80 + ₼2,00
 *
 * köhnə açarla EYNİ sətir sayılır — ikinci seçim itir, müştəri birincinin
 * qiyməti ilə iki ədəd alır. Seçilmiş variantlar açara daxil edilir; sıralanır ki,
 * [a,b] və [b,a] eyni sətri versin.
 */
export const cartLineKey = (item: any): string =>
  [
    item.type || "stock",
    item.id,
    ...[...(item.modifierIds ?? [])].sort(),
  ].join("|");

const resolveImageUrl = (rawImagePath) => {
  if (!rawImagePath) return "";
  if (typeof rawImagePath !== "string") {
    // Sayı veya obje ise boş döndür
    return "";
  }

  const path = rawImagePath.trim();
  if (!path || path === "null" || path === "undefined") return "";

  // Zaten tam URL ise direkt kullan
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  if (path.startsWith("//")) {
    return `https:${path}`;
  }

  // /public/storage/... → storage/...
  // /storage/... → storage/...
  // public/storage/... → storage/...
  // storage/... → storage/...
  // anything else → as-is
  let normalized = path
    .replace(/^\/+/, "")           // baştaki slash'ları sil
    .replace(/^public\//, "")      // public/ prefix'i sil
    .replace(/^storage\//, "");    // storage/ prefix'i sil

  const base = (img_url || "").replace(/\/+$/, ""); // sondaki slash'ları sil
  return `${base}/${normalized}`;
};

const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
};

const getProductImagePath = (product) => {
  if (!product) return "";
  const media = Array.isArray(product.media) ? product.media[0] : product.media;
  const images = Array.isArray(product.images)
    ? product.images[0]
    : product.images;
  return firstString(
    product.image,
    product.image_url,
    product.imageUrl,
    product.image_path,
    product.photo,
    product.photo_url,
    product.img,
    product.thumbnail,
    product.thumb,
    product.picture,
    product.cover,
    product.stock?.image,
    product.stock?.image_url,
    media?.url,
    media?.path,
    media?.image,
    images?.url,
    images?.path,
    images?.image,
  );
};

const unwrapListResponse = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.data)) return payload.data;

  for (const key of keys) {
    const val = payload[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.data))
      return val.data;
  }

  return [];
};

const unwrapObjectResponse = (payload, keys = []) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload))
    return null;

  for (const key of keys) {
    const value = payload[key];
    if (value && typeof value === "object" && !Array.isArray(value))
      return value;
  }

  return payload;
};

export function PosTablePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { hasPermission, permissions } = usePermissions();
  const { confirm, confirmDialog } = useConfirm();
  // Bir neçə mətbəx varsa ofisant sifarişin hansına gedəcəyini özü seçir.
  const { pickKitchen, kitchenPickerDialog } = useKitchenPicker();
  const { askApproval, approvalDialog } = useManagerApproval();

  // "Ödəniş al" düyməsi yalnız ödəniş icazəsi olanlara görünür (ofisiantda yoxdur).
  const canTakePayment = useCanTakePayment();

  // Adisyondakı sətri dəyişmək/silmək — backend `subtract-stock` route-u
  // `permission:table-order,manage-tables` tələb edir; ofisiantda `table-order` +
  // `table-orders.remove-item` var. İcazələr hələ yüklənməyibsə göstəririk
  // (server onsuz da son sözü deyir və xəta toast-la bildirilir).
  const canEditSentItems =
    permissions.length === 0 ||
    hasPermission(["table-order", "manage-tables", "table-orders.remove-item"]);

  const [stockGroups, setStockGroups] = useState<any[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);
  const [stocksSimple, setStocksSimple] = useState<any[]>([]);
  const [stockSets, setStockSets] = useState<any[]>([]);
  const [stockGroupDetails, setStockGroupDetails] = useState<any>({});
  const [stockDetails, setStockDetails] = useState<any>({});
  const [stockSetDetails, setStockSetDetails] = useState<any>({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState<any[]>([]);
  // Opsiya qrupu olan məhsul birbaşa səbətə düşmür — əvvəl modal açılır.
  // null = modal bağlıdır (məhsulların əksəriyyətində qrup yoxdur).
  const [pendingOptions, setPendingOptions] = useState<{
    product: any;
    groups: OptionGroup[];
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableName, setTableName] = useState("");
  const [waiterName, setWaiterName] = useState("");
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [orderId, setOrderId] = useState<any>(null);
  const [totalPrice, setTotalPrice] = useState({ total: 0, total_prepare: 0 });
  const [brokenImages, setBrokenImages] = useState<any>({});
  // Adisyon sətri üzərində gedən əməliyyat (paralel klikləri bloklamaq üçün).
  const [sentBusyKey, setSentBusyKey] = useState<string | null>(null);
  // Kataloq ilk dəfə yüklənənədək spinner göstəririk (əvvəl ağ ekran qalırdı).
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Game timer state
  const [gameTime, setGameTime] = useState({
    active: false,
    type: "ps",
    startTime: null,
    paused: false,
  });
  const [elapsedTime, setElapsedTime] = useState(0);

  // Load catalog lists from GET endpoints used on POS page
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const fetchOrNull = async (url) => {
          try {
            const res = await axios.get(url, getHeaders());
            return res.data;
          } catch (e: any) {
            console.error(`Error fetching ${url}:`, e);
            return null;
          }
        };

        const [groupsData, stocksData, simpleStocksData, stockSetsData] =
          await Promise.all([
            fetchOrNull(`${base_url}/stock-groups`),
            fetchOrNull(`${base_url}/stocks`),
            fetchOrNull(`${base_url}/stocks-simple`),
            fetchOrNull(`${base_url}/stock-sets`),
          ]);

        const groups = unwrapListResponse(groupsData, [
          "stock_groups",
          "groups",
          "data",
        ]);
        const fullStocks = unwrapListResponse(stocksData, ["stocks", "data"]);
        const simpleStocks = unwrapListResponse(simpleStocksData, [
          "stocks",
          "data",
        ]);
        const sets = unwrapListResponse(stockSetsData, [
          "stock_sets",
          "sets",
          "data",
        ]);

        setStockGroups(groups);
        setStocks(fullStocks);
        setStocksSimple(simpleStocks);
        setStockSets(sets);

        if (groups.length > 0) {
          setSelectedCategory((prev) => prev ?? groups[0].id);
        }

        // QEYD: burada əvvəllər HƏR qrup üçün /stock-groups/{id} və HƏR set üçün
        // /stock-sets/{id} çəkilirdi (N+1). Məqsəd məhsul şəkillərini "backfill"
        // etmək idi — amma /stocks onsuz da image_url qaytarır (cloud-da Stock
        // modelinin `$appends`-i, POS-da catalogMappers). Nəticədə hər qrup üçün
        // bir sorğu + bütün `stocks` massivini yenidən map edən setState işləyirdi
        // → 10-12 qrupda onlarla sorğu və onlarla tam re-render, ekran 1-3 saniyə
        // ağ qalırdı. Silindi: seçilmiş kateqoriyanın detalı onsuz da aşağıdakı
        // lazy effect ilə, set detalları isə lazım olanda çəkilir.
      } catch (error: any) {
        console.error("Error loading catalog:", error);
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchCatalog();

    // Admin (cloud) qiyməti/menyu dəyişəndə pos_local sync olur və catalogNotify
    // `pos:catalog-changed` yayır — menyunu reload-suz yenidən çəkirik.
    const onCatalogChanged = () => {
      fetchCatalog();
    };
    window.addEventListener("pos:catalog-changed", onCatalogChanged);
    return () =>
      window.removeEventListener("pos:catalog-changed", onCatalogChanged);
  }, []);

  // Load group detail for selected category (/stock-groups/{id})
  useEffect(() => {
    if (!selectedCategory || stockGroupDetails[selectedCategory]) return;

    const fetchStockGroupDetail = async () => {
      try {
        const response = await axios.get(
          `${base_url}/stock-groups/${selectedCategory}`,
          getHeaders(),
        );
        const groupDetail = unwrapObjectResponse(response.data, [
          "stock_group",
          "group",
          "data",
        ]);
        if (groupDetail) {
          setStockGroupDetails((prev) => ({
            ...prev,
            [selectedCategory]: groupDetail,
          }));
        }
      } catch (error: any) {
        console.error(
          `Error loading stock group detail (${selectedCategory}):`,
          error,
        );
      }
    };

    fetchStockGroupDetail();
  }, [selectedCategory, stockGroupDetails]);

  const fetchStockDetailById = async (stockId) => {
    if (!stockId) return null;
    if (stockDetails[stockId]) return stockDetails[stockId];

    try {
      const response = await axios.get(
        `${base_url}/stocks/${stockId}`,
        getHeaders(),
      );
      const stockDetail = unwrapObjectResponse(response.data, [
        "stock",
        "data",
      ]);

      if (stockDetail) {
        setStockDetails((prev) => ({ ...prev, [stockId]: stockDetail }));
        return stockDetail;
      }
    } catch (error: any) {
      console.error(`Error loading stock detail (${stockId}):`, error);
    }

    return null;
  };

  // Fetch table info and orders
  const fetchTableOrders = useCallback(async () => {
      try {
        const response = await axios.get(
          `${base_url}/tables/${id}/order`,
          getHeaders(),
        );
        setTableName(response.data.table.name);
        const orders = response.data.table.orders;
        setOrderId(orders?.[0]?.order_id ?? null);
        if (orders.length > 0) {
          setTotalPrice({
            total: orders[0].total_price || 0,
            total_prepare: orders[0].total_prepayment || 0,
          });
          const allItems = orders.flatMap((order) => [
            ...order.stocks.map((s) => ({
              id: s.id,
              name: s.name,
              quantity: s.quantity,
              price: s.price,
              pivot_id: s.pivot_id,
              type: "stock",
            })),
            ...order.sets.map((s) => ({
              id: s.id,
              name: s.name,
              quantity: s.quantity,
              price: s.price,
              pivot_id: s.pivot?.id,
              type: "set",
            })),
          ]);
          setOrderDetails(allItems);

          const uniqueSetIds = [
            ...new Set(
              allItems
                .filter((item) => item.type === "set")
                .map((item) => item.id),
            ),
          ];
          uniqueSetIds.forEach((setId) => {
            axios
              .get(`${base_url}/stock-sets/${setId}`, getHeaders())
              .then((setResponse) => {
                const setDetail = unwrapObjectResponse(setResponse.data, [
                  "stock_set",
                  "set",
                  "data",
                ]);
                if (!setDetail) return;

                setStockSetDetails((prev: any) => {
                  if (prev[setId as any]) return prev;
                  return { ...prev, [setId as any]: setDetail };
                });
              })
              .catch((error) => {
                console.error(
                  `Error loading stock set detail (${setId}):`,
                  error,
                );
              });
          });
        } else {
          // Sifariş qalmadı (məs. başqa cihazdan bütün məhsullar silindi /
          // hesab bağlandı) — ekranı boşalt, köhnə siyahı qalmasın.
          setOrderDetails([]);
          setTotalPrice({ total: 0, total_prepare: 0 });
        }
      } catch (error: any) {
        console.error("Error fetching table orders:", error);
      }
  }, [id]);

  useEffect(() => {
    fetchTableOrders();
  }, [fetchTableOrders]);

  // Real-time: pos_local `orders.*` / `tables.*` event-i gələndə masanın sifariş
  // məzmununu yenidən çək — başqa cihazdan (yaxud QR-dan) məhsul əlavə/silinəndə
  // ekran özü yenilənir, əl ilə refresh lazım deyil.
  usePosOrderRealtime(fetchTableOrders);

  // Timer effect
  useEffect(() => {
    let interval;
    if (gameTime.active && !gameTime.paused) {
      interval = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameTime.active, gameTime.paused]);

  const stockAmountMap = stocksSimple.reduce((acc, item) => {
    acc[item.id] = item.amount;
    return acc;
  }, {});

  const productsByCategory =
    selectedCategory === "sets"
      ? stockSets.map((s) => ({ ...s, type: "set" }))
      : stocks
        .filter((stock) => {
          if (selectedCategory === null) return true;
          const groupId =
            stock.stock_group_id ||
            stock.stockGroup?.id ||
            stock.stock_group?.id ||
            stock.group?.id;
          return Number(groupId) === Number(selectedCategory);
        })
        .map((stock) => {
          const groupDetail = stockGroupDetails[selectedCategory as any];
          const groupStocks = Array.isArray(groupDetail?.stocks)
            ? groupDetail.stocks
            : [];
          const fromGroup = groupStocks.find(
            (item) => Number(item?.id) === Number(stock.id),
          );
          const fromDetail = stockDetails[stock.id];
          return {
            ...(fromGroup || {}),
            ...stock,
            ...(fromDetail || {}),
            image:
              getProductImagePath(stock) ||
              getProductImagePath(fromGroup) ||
              getProductImagePath(fromDetail),
          };
        });

  // Filter products by search
  const filteredProducts = productsByCategory.filter((p) =>
    resolveName(p.name).toLowerCase().includes(searchQuery.toLowerCase()),
  );

  /** Hazır sətri səbətə qoyur; eyni açarlı sətir varsa miqdarı artırır. */
  const pushToCart = (line: any, quantity: number) => {
    const key = cartLineKey(line);
    setCart((prevCart) => {
      const existing = prevCart.find((item) => cartLineKey(item) === key);
      if (existing) {
        return prevCart.map((item) =>
          cartLineKey(item) === key
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      }
      return [...prevCart, { ...line, quantity, notes: [] }];
    });
  };

  const addToCart = async (product) => {
    const isSet = product.type === "set";
    const detail = isSet ? null : await fetchStockDetailById(product.id);

    const preparedProduct = {
      ...product,
      ...(detail || {}),
      type: isSet ? "set" : "stock",
      price: Number(detail?.price ?? product.price ?? 0),
    };

    // Opsiya qrupları YALNIZ tək məhsul endpoint-indən gəlir (siyahıda olmur),
    // ona görə `detail` oxunur. Qrup yoxdursa köhnə axın — modal açılmır.
    const groups: OptionGroup[] = Array.isArray(detail?.modifierGroups)
      ? detail.modifierGroups
      : [];

    if (groups.length > 0) {
      setPendingOptions({ product: preparedProduct, groups });
      return;
    }

    pushToCart({ ...preparedProduct, modifierIds: [], modifierLabels: [] }, 1);
  };

  /** Modal təsdiqi → seçilmiş variantlarla yeni səbət sətri. */
  const confirmOptions = (selection: OptionSelection) => {
    const pending = pendingOptions;
    if (!pending) return;
    setPendingOptions(null);
    pushToCart(
      {
        ...pending.product,
        // Sətrin qiyməti = baza + seçimlərin fərqi. pos_local eyni hesabı
        // müstəqil aparır; bu yalnız ekranda göstərilən məbləğdir.
        price: Number(pending.product.price ?? 0) + selection.priceDelta,
        basePrice: Number(pending.product.price ?? 0),
        modifierIds: selection.modifierIds,
        modifierLabels: selection.labels,
      },
      selection.quantity,
    );
  };

  const removeFromCart = (lineKey: string) => {
    setCart((prevCart) => prevCart.filter((item) => cartLineKey(item) !== lineKey));
  };

  const updateQuantity = (lineKey: string, delta: number) => {
    setCart((prevCart) =>
      prevCart
        .map((item) => {
          if (cartLineKey(item) === lineKey) {
            const newQty = Math.max(0, item.quantity + delta);
            return { ...item, quantity: newQty };
          }
          return item;
        })
        .filter((item) => item.quantity > 0),
    );
  };

  const addNoteToItem = (lineKey: string, note: string) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (cartLineKey(item) === lineKey) {
          const notes = item.notes || [];
          if (notes.includes(note))
            return { ...item, notes: notes.filter((n) => n !== note) };
          return { ...item, notes: [...notes, note] };
        }
        return item;
      }),
    );
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ── Adisyon (artıq mətbəxə göndərilmiş sətirlər) redaktəsi ────────────────
  // Gateway `subtract-stock`-u hər iki istiqamətdə dəstəkləyir:
  //   increase=false → azalt (0-a düşsə sətir tamamilə silinir)
  //   increase=true  → artır
  // Bax: src/api/pos/routes.ts (POST /tables/:id/subtract-stock).
  const sentItemKey = (item) =>
    `${item.type || "stock"}-${item.pivot_id ?? item.id}`;

  const changeSentQuantity = async (item, delta) => {
    if (sentBusyKey) return; // paralel klikləri blokla (miqdar yarışmasın)
    if (delta < 0 && item.quantity <= 1) {
      const ok = await confirm({
        message: t("pos.removeSentConfirm"),
        confirmLabel: t("common.delete"),
        variant: "danger",
      });
      if (!ok) return;
    }
    setSentBusyKey(sentItemKey(item));
    try {
      await axios.post(
        `${base_url}/tables/${id}/subtract-stock`,
        {
          stock_id: item.id,
          quantity: 1,
          pivotId: item.pivot_id,
          increase: delta > 0,
        },
        getHeaders(),
      );
      await fetchTableOrders();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("pos.sentItemUpdateFailed"),
      );
    } finally {
      setSentBusyKey(null);
    }
  };

  const removeSentItem = async (item) => {
    if (sentBusyKey) return;
    const ok = await confirm({
      message: t("pos.removeSentConfirm"),
      confirmLabel: t("common.delete"),
      variant: "danger",
    });
    if (!ok) return;
    setSentBusyKey(sentItemKey(item));
    try {
      // Bütün sətri silmək = cari miqdar qədər azaltmaq (0 → sətir void olur).
      const payload: Record<string, unknown> = {
        stock_id: item.id,
        quantity: item.quantity,
        pivotId: item.pivot_id,
        increase: false,
      };

      try {
        await axios.post(
          `${base_url}/tables/${id}/subtract-stock`,
          payload,
          getHeaders(),
        );
      } catch (error: any) {
        // ═══ MENECER TƏSDİQİ ═══
        //
        // Sətir artıq mətbəxə gedibsə server təsdiq tələb edir. Əvvəllər
        // yeganə çıxış yol menecerin hesabına GİRMƏK idi — PIN-i öyrənən
        // ofisant sonra hər şeyi edə bilirdi və jurnalda menecerin adı qalırdı.
        //
        // İndi ofisant öz hesabında qalır: menecer yalnız PIN yazır, token
        // 60 saniyəlik və TƏK İSTİFADƏLİKDİR, məhz bu sətrə bağlıdır.
        if (!needsManagerApproval(error)) throw error;

        const token = await askApproval({
          scope: APPROVAL_SCOPES.voidItemAfterKitchen,
          context: { orderId, itemId: item.pivot_id },
          title: t("approval.voidItemTitle"),
          detail: `${resolveName(item.name)} × ${item.quantity}`,
        });
        if (!token) return; // ləğv edildi — sətir toxunulmadan qalır

        await axios.post(
          `${base_url}/tables/${id}/subtract-stock`,
          { ...payload, approval_token: token },
          getHeaders(),
        );
      }

      await fetchTableOrders();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("pos.sentItemUpdateFailed"),
      );
    } finally {
      setSentBusyKey(null);
    }
  };

  const sendToKitchen = async () => {
    if (cart.length === 0) return;

    // Restoranda bir neçə mətbəx varsa (Bar, Əsas Mətbəx...) sifariş fişi
    // hansı printerdən çıxacaq — ofisant seçir. Tək mətbəx varsa dialoq
    // açılmır, heç biri yoxdursa avtomatik marşrut işləyir.
    const choice = await pickKitchen();
    if (choice === null) return; // ləğv edildi

    try {
      for (const item of cart) {
        // Səbətdəki qeydlər (Acılı, Buzsuz...) → pos_local item notes → KOT-da çap.
        const notes = Array.isArray(item.notes)
          ? item.notes.filter(Boolean).join(", ")
          : "";
        await axios.post(
          `${base_url}/tables/${id}/add-stock`,
          {
            stock_id: item.id,
            quantity: item.quantity,
            ...(notes ? { notes } : {}),
            ...(Array.isArray(item.modifierIds) && item.modifierIds.length > 0
              ? { modifier_ids: item.modifierIds }
              : {}),
            ...(choice.kitchenId ? { kitchen_id: choice.kitchenId } : {}),
          },
          getHeaders(),
        );
      }
      toast.success(
        choice.kitchenName
          ? `${t("pos.toast.sentKitchen")} — ${choice.kitchenName}`
          : t("pos.toast.sentKitchen"),
      );
      setCart([]);
      // Refresh orders
      const response = await axios.get(
        `${base_url}/tables/${id}/order`,
        getHeaders(),
      );
      const orders = response.data.table.orders;
      if (orders.length > 0) {
        setTotalPrice({
          total: orders[0].total_price || 0,
          total_prepare: orders[0].total_prepayment || 0,
        });
      }
    } catch (error: any) {
      console.error("Error sending order:", error);
      toast.error(t("pos.toast.sendFailed"));
    }
  };

  // Qəbz çapı — POS rejimində termal çek aparatından (brauzersiz).
  const handlePrintReceipt = async () => {
    try {
      if (await printReceiptThermal(orderId)) {
        toast.success(t("dock.receiptSent"));
      }
    } catch (error: any) {
      toast.error(error?.message || t("dock.receiptFailed"));
    }
  };

  // Ödəniş: /masalar-a qayıdıb həmin masanın ödəniş modalını avtomatik aç.
  const handleOpenPayment = () => {
    navigate("/masalar", { state: { openPaymentForTable: id } });
  };

  const cartTotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const totalAmount = Number(totalPrice.total || 0) + cartTotal;

  const markImageBroken = (imageKey) => {
    setBrokenImages((prev) => {
      if (prev[imageKey]) return prev;
      return { ...prev, [imageKey]: true };
    });
  };

  return (
    <>
      <Helmet>
        <title>{tableName || `${t("common.table")} ${id}`} | Smartcafe</title>
      </Helmet>
      <div
        className="flex h-[calc(100vh-80px)] overflow-hidden text-gray-900 dark:text-gray-100"
        style={{ backgroundColor: "var(--bg-body)" }}
      >
        {/* LEFT COLUMN: Categories & Products */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Header Bar */}
          <div className="h-16 bg-white/85 dark:bg-[#111318]/90 backdrop-blur-2xl border-b border-gray-100 dark:border-white/[0.06] flex items-center justify-between px-6 shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/masalar")}
                className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-500" />
              </button>
              <h1 className="text-xl font-bold text-gray-800">
                {tableName || `${t("common.table")} ${id}`}
              </h1>
              <div className="h-6 w-px bg-gray-200 dark:bg-white/10"></div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="w-4 h-4" />
                <span>{waiterName || t("pos.waiter")}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("pos.searchProduct")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 bg-gray-100 dark:bg-white/5 border border-transparent dark:border-white/[0.06] rounded-xl text-sm focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-[var(--primary)] outline-none w-64 transition-all text-gray-900 dark:text-white placeholder:text-gray-500"
                  />
                </div>
                {stockSets.length > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1 text-right">
                    {t("pos.setsLoaded", { n: stockSets.length, m: Object.keys(stockSetDetails).length })}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Categories Sidebar */}
            <div className="w-24 bg-white/85 dark:bg-[#111318]/90 border-r border-gray-100 dark:border-white/[0.06] overflow-y-auto flex flex-col gap-2 p-2 shrink-0">
              {stockSets.length > 0 && (
                <button
                  onClick={() => setSelectedCategory("sets")}
                  className={`
                    flex flex-col items-center justify-center p-3 rounded-xl transition-all gap-2 aspect-square
                    ${selectedCategory === "sets"
                      ? "bg-[var(--primary)] text-white shadow-lg shadow-orange-500/20 scale-105"
                      : "bg-gray-50 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:text-[var(--primary)]"
                    }
                  `}
                >
                  <span className="text-2xl">🍔</span>
                  <span className="text-[10px] font-bold text-center leading-tight">
                    {t("pos.menusSets")}
                  </span>
                </button>
              )}
              {stockGroups.map((cat, index) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`
                    flex flex-col items-center justify-center p-3 rounded-xl transition-all gap-2 aspect-square
                    ${selectedCategory === cat.id
                      ? "bg-[var(--primary)] text-white shadow-lg shadow-orange-500/20 scale-105"
                      : "bg-gray-50 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:text-[var(--primary)]"
                    }
                  `}
                >
                  <span className="text-2xl">
                    {categoryIcons[index % categoryIcons.length]}
                  </span>
                  <span className="text-[10px] font-bold text-center leading-tight">
                    {resolveName(stockGroupDetails[cat.id]?.name || cat.name)}
                  </span>
                </button>
              ))}
            </div>

            {/* Products Grid */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50 dark:bg-[#09090b]/40">
              {catalogLoading && filteredProducts.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <PosLoader size="lg" label={t("pos.loadingMenu")} />
                </div>
              ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-20">
                {filteredProducts.map((product) => {
                  const imageKey = `product-${product.id}`;
                  const imageSrc = resolveImageUrl(
                    getProductImagePath(product),
                  );
                  const showImage = !!imageSrc && !brokenImages[imageKey];

                  return (
                    <div
                      key={product.id}
                      onClick={() => {
                        void addToCart(product);
                      }}
                      className="bg-white dark:bg-[#161618] p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-white/[0.06] hover:shadow-md hover:border-orange-200 dark:hover:border-orange-500/40 hover:-translate-y-1 transition-all cursor-pointer group"
                    >
                      <div className="aspect-[4/3] rounded-xl bg-gray-100 dark:bg-white/[0.04] mb-3 overflow-hidden relative">
                        {showImage ? (
                          <img
                            src={imageSrc}
                            alt={resolveName(product.name)}
                            onError={() => markImageBroken(imageKey)}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600 text-4xl">
                            {product.type === "set"
                              ? "🍔"
                              : categoryIcons[
                              stockGroups.findIndex(
                                (g) =>
                                  Number(g.id) ===
                                  Number(product.stock_group_id),
                              ) % categoryIcons.length
                              ] || "🍽️"}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-white/90 dark:bg-black/60 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-bold text-gray-900 dark:text-white shadow-sm">
                          ₼{product.price}
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {resolveName(product.name)}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t("pos.stock")}:{" "}
                        {stockAmountMap[product.id] ??
                          product.amount ??
                          product.stock ??
                          0}
                      </p>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Cart / Order / Game */}
        <div className="w-[400px] bg-white dark:bg-[#111318] border-l border-gray-100 dark:border-white/[0.06] flex flex-col shadow-2xl dark:shadow-black/40 z-20 shrink-0 h-full">
          {/* Game / Time Control Panel */}
          <div className="p-4 shrink-0" style={{ backgroundColor: "#111827" }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2">
                <Gamepad2 className="w-5 h-5 text-[var(--primary)]" />
                {t("pos.gameTime")}
              </h3>
              <div className="font-mono text-2xl font-bold text-[var(--primary)] tabular-nums">
                {formatTime(elapsedTime)}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {!gameTime.active ? (
                <>
                  <button
                    onClick={() =>
                      setGameTime({
                        active: true,
                        type: "ps",
                        startTime: Date.now(),
                        paused: false,
                      })
                    }
                    className="col-span-2 bg-green-600 hover:bg-green-500 py-2 rounded-lg font-bold text-xs transition-colors"
                  >
                    {t("pos.psStart")}
                  </button>
                  <button className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg font-bold text-xs transition-colors">
                    {t("pos.min30")}
                  </button>
                  <button className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg font-bold text-xs transition-colors">
                    {t("pos.hour1")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() =>
                      setGameTime((prev) => ({ ...prev, paused: !prev.paused }))
                    }
                    className={`col-span-2 py-2 rounded-lg font-bold text-xs transition-colors flex items-center justify-center gap-1 ${gameTime.paused ? "bg-green-600" : "bg-yellow-600"}`}
                  >
                    {gameTime.paused ? (
                      <Play className="w-3 h-3" />
                    ) : (
                      <Pause className="w-3 h-3" />
                    )}
                    {gameTime.paused ? t("pos.resume") : t("pos.pause")}
                  </button>
                  <button
                    onClick={() => {
                      setGameTime({
                        active: false,
                        type: "ps",
                        startTime: null,
                        paused: false,
                      });
                      setElapsedTime(0);
                      toast.success(t("pos.gameEnded"));
                    }}
                    className="col-span-2 bg-red-600 hover:bg-red-500 py-2 rounded-lg font-bold text-xs transition-colors flex items-center justify-center gap-1"
                  >
                    <StopCircle className="w-3 h-3" />
                    {t("pos.finish")}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Customer Selector */}
          <button className="mx-4 mt-4 p-3 bg-orange-50 dark:bg-orange-500/10 text-[var(--primary)] rounded-xl flex items-center justify-between font-bold text-sm hover:bg-orange-100 dark:hover:bg-orange-500/15 transition-colors border border-orange-100 dark:border-orange-500/20">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {t("pos.noCustomer")}
            </span>
            <Search className="w-4 h-4" />
          </button>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Adisyon — masaya artıq göndərilmiş sətirlər (səbətdən ayrı).
                Real-time yenilənir: usePosOrderRealtime → fetchTableOrders. */}
            {orderDetails.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between px-1">
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t("pos.sentItems")}
                  </h4>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {t("pos.sentItemsHint")}
                  </span>
                </div>

                {orderDetails.map((item) => {
                  const key = sentItemKey(item);
                  const busy = sentBusyKey === key;
                  return (
                    <div
                      key={key}
                      className={`bg-white dark:bg-white/[0.03] p-3 rounded-xl border border-gray-200 dark:border-white/[0.08] ${busy ? "opacity-50" : ""}`}
                    >
                      <div className="flex justify-between items-start gap-2 mb-1">
                        <h4 className="font-bold text-gray-900 dark:text-white text-sm truncate">
                          {resolveName(item.name)}
                        </h4>
                        <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap">
                          ₼{item.price * item.quantity}
                        </span>
                      </div>
                      {/* Göndərilmiş sətrin seçimləri — eyni adlı iki sətri
                          (böyük/kiçik) ayırd etmək üçün. */}
                      {Array.isArray(item.modifiers) && item.modifiers.length > 0 && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 leading-snug">
                          {item.modifiers
                            .map((m: any) => resolveName(m.name))
                            .join(" · ")}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          ₼{item.price} × {item.quantity}
                        </p>
                        {canEditSentItems && (
                          <div className="flex items-center gap-1">
                            <div className="flex items-center gap-2 bg-gray-50 dark:bg-[#111318] rounded-lg p-1 border border-gray-200 dark:border-white/[0.08]">
                              <button
                                disabled={busy}
                                onClick={() => changeSentQuantity(item, -1)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded text-gray-500 dark:text-gray-400 disabled:opacity-40"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="font-bold text-sm w-4 text-center text-gray-900 dark:text-white">
                                {item.quantity}
                              </span>
                              <button
                                disabled={busy}
                                onClick={() => changeSentQuantity(item, 1)}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded text-gray-500 dark:text-gray-400 disabled:opacity-40"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <button
                              disabled={busy}
                              onClick={() => removeSentItem(item)}
                              title={t("common.delete")}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {cart.length > 0 && (
                  <div className="pt-2 px-1">
                    <h4 className="text-xs font-black uppercase tracking-wider text-[var(--primary)]">
                      {t("pos.cartPending")}
                    </h4>
                  </div>
                )}
              </div>
            )}

            {cart.length === 0 ? (
              <div
                className={`flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 space-y-4 px-8 text-center ${
                  // Adisyon doludursa böyük boş-səbət ekranı yer tutmasın.
                  orderDetails.length > 0 ? "py-6 opacity-70" : "h-full"
                }`}
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-[2rem] bg-[var(--primary)]/20 blur-xl" />
                  <div className="relative w-20 h-20 rounded-[2rem] bg-gradient-to-br from-orange-500/15 to-amber-500/10 border border-orange-500/20 flex items-center justify-center">
                    <ShoppingCart className="w-9 h-9 text-[var(--primary)]" />
                  </div>
                </div>
                <div>
                  <p className="text-base font-black text-gray-700 dark:text-gray-200">
                    {t("pos.cartEmpty")}
                  </p>
                  <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                    {t("pos.cartEmptyHint")}
                  </p>
                </div>
              </div>
            ) : (
              cart.map((item) => {
                const lineKey = cartLineKey(item);
                const imageKey = `cart-${lineKey}-${item.pivot_id || ""}`;
                const imageSrc = resolveImageUrl(getProductImagePath(item));
                const showImage = !!imageSrc && !brokenImages[imageKey];

                return (
                  <div
                    key={lineKey}
                    className="bg-gray-50 dark:bg-white/[0.04] p-3 rounded-xl border border-gray-100 dark:border-white/[0.06] group"
                  >
                    <div className="flex gap-3 mb-2">
                      {/* Product Image */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-200 dark:bg-white/[0.08]">
                        {showImage ? (
                          <img
                            src={imageSrc}
                            alt={resolveName(item.name)}
                            onError={() => markImageBroken(imageKey)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 text-2xl">
                            🍽️
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-bold text-gray-900 text-sm truncate pr-2">
                            {resolveName(item.name)}
                          </h4>
                          <span className="font-bold text-gray-900 whitespace-nowrap">
                            ₼{item.price * item.quantity}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">
                          ₼{item.price} × {item.quantity}
                        </p>
                        {/* Seçilmiş variantlar — ofisiant hansı sətrin nə olduğunu
                            görməsə eyni adlı iki sətri ayırd edə bilməzdi. */}
                        {Array.isArray(item.modifierLabels) &&
                          item.modifierLabels.length > 0 && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 leading-snug">
                              {item.modifierLabels.join(" · ")}
                            </p>
                          )}

                        {/* Quantity Controls & Delete */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 bg-white dark:bg-[#111318] rounded-lg p-1 border border-gray-200 dark:border-white/[0.08]">
                            <button
                              onClick={() =>
                                updateQuantity(lineKey, -1)
                              }
                              className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded text-gray-500 dark:text-gray-400"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="font-bold text-sm w-4 text-center text-gray-900 dark:text-white">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                updateQuantity(lineKey, 1)
                              }
                              className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded text-gray-500 dark:text-gray-400"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(lineKey)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Quick Notes */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {quickNoteKeys.map((noteKey) => {
                        const note = t(noteKey);
                        return (
                          <button
                            key={noteKey}
                            onClick={() =>
                              addNoteToItem(lineKey, note)
                            }
                            className={`
                          text-[10px] px-2 py-1 rounded border transition-colors
                          ${item.notes?.includes(note)
                                ? "bg-orange-100 border-orange-200 text-orange-700 font-bold"
                                : "bg-white dark:bg-[#111318] border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/20"
                              }
                        `}
                          >
                            {note}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Bottom Actions */}
          <div className="p-4 bg-white dark:bg-[#111318] border-t border-gray-100 dark:border-white/[0.06] space-y-3 shadow-[0_-10px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_-10px_40px_rgba(0,0,0,0.35)] z-30">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>{t("pay.subtotal")}</span>
              <span>₼{totalAmount}</span>
            </div>
            <div className="flex justify-between items-center text-2xl font-black text-gray-900 dark:text-white">
              <span>{t("pos.total")}</span>
              <span>₼{totalAmount}</span>
            </div>

            <div
              className={`grid gap-3 ${canTakePayment ? "grid-cols-3" : "grid-cols-2"}`}
            >
              <button
                onClick={sendToKitchen}
                className="py-4 rounded-xl font-bold bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
              >
                {t("pos.kitchen")}
              </button>
              <button
                onClick={handlePrintReceipt}
                disabled={!orderId}
                className="py-4 rounded-xl font-bold bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("checkout.receipt")}
              </button>
              {canTakePayment && (
                <button
                  onClick={handleOpenPayment}
                  className="py-4 rounded-xl font-bold bg-[var(--primary)] text-white hover:bg-orange-600 shadow-lg shadow-orange-500/25 transition-all active:scale-95"
                >
                  {t("pos.takePayment")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {approvalDialog}
      {pendingOptions && (
        <ProductOptionsModal
          productName={resolveName(pendingOptions.product.name)}
          basePrice={Number(pendingOptions.product.price ?? 0)}
          groups={pendingOptions.groups}
          onCancel={() => setPendingOptions(null)}
          onConfirm={confirmOptions}
        />
      )}
      {confirmDialog}
      {kitchenPickerDialog}
    </>
  );
}
